import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import ProductGrid from '../components/ProductGrid';
import './SearchResults.css';
import { Product } from '../features/products/productModels';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllProducts } from '../features/products/productSelectors';
import { AppDispatch } from '../features/store';
import { getAllProducts } from '../features/products/productThunks';
import Loader from '../components/Loader';

const SearchResults: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [wishlistItems, setWishlistItems] = useState<number[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const allProducts=useSelector(selectAllProducts);
  const dispatch:AppDispatch=useDispatch();
  const query = searchParams.get('q') || '';
  useEffect(()=>{
    dispatch(getAllProducts());
  },[]);

  useEffect(()=>{
    if(allProducts.length > 0){
      setFilteredProducts([...allProducts] as Product[]);
      setIsLoading(false);
    }
  },[allProducts]);
  // Filter products based on search query
  useEffect(() => {
    if (!query.trim()) {
      setFilteredProducts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Simulate API delay
    const timer = setTimeout(() => {
      const results = allProducts.filter((product:Product) =>
        product.productName.toLowerCase().includes(query.toLowerCase()) ||
        product.brand.toLowerCase().includes(query.toLowerCase()) ||
        product.description.toLowerCase().includes(query.toLowerCase()) ||
        product.categoryName?.toLowerCase().includes(query.toLowerCase())
      );
      
      setFilteredProducts(results);
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [query,allProducts]);

  const handleAddToCart = (product: Product) => {
    // addToCart(product, 1);
  };

  const handleToggleWishlist = (productId: number) => {
    setWishlistItems(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  if (isLoading) {
    return (
      <Loader size="large" text={t('searchResults.loadingProducts')} />
    );
  }

  return (
    <div className="search-results-page">
      <div className="container">
        <div className="search-header">
          <h1>{t('searchResults.title')}</h1>
          {query && (
            <p className="search-query">
              {t('searchResults.showingResultsFor')} <strong>"{query}"</strong>
            </p>
          )}
        </div>

        {filteredProducts.length > 0 ? (
          <ProductGrid
            products={filteredProducts}
            onAddToCart={handleAddToCart}
            onToggleWishlist={handleToggleWishlist}
            wishlistItems={wishlistItems}
            showFilters={true}
          />
        ) : (
          <div className="no-results">
            <div className="no-results-content">
              <h2>{t('searchResults.noProductsFound')}</h2>
              <p>{t('searchResults.noProductsMatching', { query })}</p>
              <div className="suggestions">
                <h3>{t('searchResults.tryTheseSuggestions')}</h3>
                <ul>
                  <li>{t('searchResults.checkSpelling')}</li>
                  <li>{t('searchResults.tryDifferentKeywords')}</li>
                  <li>{t('searchResults.useGeneralTerms')}</li>
                  <li>{t('searchResults.browseCategories')}</li>
                </ul>
              </div>
              <div className="search-actions">
                <button 
                  className="browse-categories"
                  onClick={() => window.history.back()}
                >
                  {t('searchResults.browseCategoriesButton')}
                </button>
                <button 
                  className="clear-search"
                  onClick={() => window.location.href = '/'}
                >
                  {t('searchResults.startOver')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
